import { type EnvironmentId } from "@t3tools/contracts";
import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import {
  type DraftId,
  unassignedDraftLogicalKey,
  useComposerDraftStore,
} from "../composerDraftStore";
import { newDraftId, newThreadId } from "../lib/utils";

/**
 * Returns the environment's project-less home draft, creating it on first use.
 * The draft is reused until it is assigned a project or promoted to a thread.
 */
export function useResolveHomeDraftId(): (environmentId: EnvironmentId) => DraftId {
  return useCallback((environmentId: EnvironmentId) => {
    const store = useComposerDraftStore.getState();
    const logicalProjectKey = unassignedDraftLogicalKey(environmentId);
    const existing = store.getDraftSessionByLogicalProjectKey(logicalProjectKey);
    if (existing && existing.promotedTo == null && existing.projectId === null) {
      return existing.draftId;
    }
    const draftId = newDraftId();
    store.setLogicalProjectDraftThreadId(
      logicalProjectKey,
      { environmentId, projectId: null },
      draftId,
      {
        threadId: newThreadId(),
        createdAt: new Date().toISOString(),
      },
    );
    return draftId;
  }, []);
}

export function useOpenHomeDraft(): (
  environmentId: EnvironmentId,
  options?: { replace?: boolean },
) => Promise<void> {
  const resolveHomeDraftId = useResolveHomeDraftId();
  const navigate = useNavigate();
  return useCallback(
    (environmentId: EnvironmentId, options?: { replace?: boolean }) =>
      navigate({
        to: "/draft/$draftId",
        params: { draftId: resolveHomeDraftId(environmentId) },
        replace: options?.replace ?? false,
      }),
    [navigate, resolveHomeDraftId],
  );
}
